import moment from 'moment';
import { timeFormat } from './dateFormat';
import * as time from './timeCalc';
import commonStore from '../library/stores/commonStore';

export const scheduleStatuses = {
    PENDING_INVITED: 'pending-invited',
    PENDING_ACCEPTED: 'pending-accepted',
    PENDING_USER_REJECTED: 'pending-user-rejected',
    PENDING_USER_INVITED: 'pending-user-invited',
    PENDING_DRAFT: 'pending',
    ASSIGNED: 'assigned',
};

export const getTimeFromShift = (shift, isStart) => {
    if (shift.source === 'absence' || shift.source === 'vacation') {
        return `${shift.for_date} ${time.minutesToTimeInput(isStart ? shift.start_time : (shift.end_time - 2), false)}:00`;
    }
    return `${shift.for_date} ${time.minutesToTimeInput(isStart ? shift.start_time : shift.end_time + 1, false)}:00`;
};

const transfromTimelogs = (timelogArr, dateTimeRules) => {
    let timelogEntries = [], timelogsToBeApproved = [], timelogStatus = null, hasLogFromDiffLocation = false;
    if (!timelogArr) return {
        timelogEntries,
        timelogsToBeApproved,
        timelogStatus,
        timelogCount: 0,
        timelogsToBeApprovedCount: 0,
        hasLogFromDiffLocation
    }

    timelogEntries = timelogArr.map(timelog => {
        if (!timelogStatus)
            timelogStatus = timelog.timelog_status;

        if (timelogStatus && timelogStatus !== timelog.timelog_status)
            timelogStatus = 'none';

        if (timelog.differentTask)
            hasLogFromDiffLocation = timelog.differentTask;

        return {
            timelogId: timelog.timelog_id,
            timelogFrom: timelog.timelog_from,
            timelogTo: timelog.timelog_to,
            timelogStatus: timelog.timelog_status,
            timelogTitle:
                `${timeFormat(timelog.timelog_from, dateTimeRules)} - ${timelog.timelog_status === 'draft' ? '' :
                    timeFormat(timelog.timelog_to, dateTimeRules)}`,
            timelogLoggedFromDiffLocation: timelog.differentTask
        }
    })

    timelogsToBeApproved = timelogEntries.filter(ele => ele.timelogStatus !== 'draft' && ele.timelogStatus !== 'approved');

    return {
        timelogEntries,
        timelogsToBeApproved,
        timelogStatus,
        timelogCount: timelogEntries.length,
        timelogsToBeApprovedCount: timelogsToBeApproved.length,
        hasLogFromDiffLocation
    }
}

export const transformToSchedulerEvent = (shift, dateTimeRules) => {
    // Special handling for urgent calls
    if (shift.source === 'urgent_call') {
        return {
            id: shift.id,
            source: shift.source,
            is_field_operative: shift?.is_field_operative,
            start: shift.for_date + ' 00:00:00',
            end: shift.for_date + ' 23:59:59',
            resourceId: shift.assignee_id || 0,
            title: shift.source,
            bgColor: shift.is_field_operative ? 'green' : '#2550AC',
            showPopover: true,
            forDate: shift.for_date,
            resourceName: shift.assignee_name,
            employementGrade: shift.data?.employement_grade || 0,
            isAllDay: true,
            syncStatus: shift.sync_status,
            urgentCallDetails: shift.source === 'urgent_call'
                ? { start_time: shift.start_time, end_time: shift.end_time, is_roster_registered: shift?.data?.is_roster_registered, is_copied: shift?.data?.is_copied }
                : {}

        };
    }

    if (shift.source === 'free_code') {
        return {
            id: shift.id,
            source: shift.source,
            start: shift.for_date + ' 00:00:00',
            end: shift.for_date + ' 23:59:59',
            resourceId: shift.assignee_id || 0,
            title: shift.data?.free_code || '',
            freeCodeName: shift.data?.free_code_name || '',
            bgColor: '#ff9e9e',
            showPopover: true,
            forDate: shift.for_date,
            resourceName: shift.assignee_name,
            isAllDay: true,
        };
    }

    const { timelogEntries, timelogsToBeApproved, timelogCount,
        timelogStatus, timelogsToBeApprovedCount, hasLogFromDiffLocation } = transfromTimelogs(shift.timelogs, dateTimeRules);
    const todayStr = moment().format('YYYY-MM-DD');
    const isPastShift = shift.for_date <= todayStr;
    const event = {
        id: shift.id,
        source: shift.source,
        start: getTimeFromShift(shift, true),
        end: getTimeFromShift(shift, false),
        resourceId: shift.assignee_id || 0,
        title: `${timeFormat(shift.start_time, dateTimeRules)} - ${timeFormat(shift.end_time, dateTimeRules)}`,
        bgColor: '#D9D9D9',
        showPopover: true,
        projectName: shift.project_name,
        taskName: shift.task_name,
        addBonus: (shift && shift.data && shift.data.addBonus) || false,
        workLogStatus: ['approved', 'rejected'].includes(timelogStatus) ? timelogStatus : shift.work_log_status,
        forDate: shift.for_date,
        show_to_member: shift.show_to_member,
        resourceName: shift.assignee_name,
        employeeType: shift.employee_type,
        userGroup: shift.user_group,
        shiftCount: shift.shift_creation_count ? shift.shift_creation_count : 1,
        created_by: shift.created_by,
        created_by_name: shift.created_by_name,
        timelogTitle: !!timelogCount ?
            timelogCount === 1 ?
                timelogEntries[0].timelogTitle : 'Multiple entries'
            : !shift.assignee_id ? 'Pending' :
                time.compareDates(shift.for_date, new Date()) > 0 ? 'Scheduled' : 'Missing Log',
        timelogCount,
        timelogStatus,
        timelogEntries,
        timelogsToBeApproved,
        timelogsToBeApprovedCount,
        hasLogFromDiffLocation,
        isEditable: shift.is_editable,
        isCopied: shift?.data?.history?.reverse().find(ele => ele.action === 'copy') ?? false,
        employementGrade: shift.source === 'absence' ? shift.data.employement_grade : null,
        companyShiftId: shift.company_shift_id,
        taskId: shift.task_id,
        startTime: shift.start_time,
        endTime: shift.end_time,
        isMultiday: shift.is_multiday ? true : false,
        isRosterShiftsPublishedAndEdited: (shift.data && shift.data.isPublishedAndEdited) || false,
        isApproved: shift.is_approved || false,
        // a future schedule entry always forces auto time recording on server-side, regardless of this
        // member's own profile toggle - the badge must reflect that instead of hiding behind it
        allowShiftForAutoTimesheet: shift.allow_shift_for_auto_timesheet === true &&
            (shift.is_future_schedule_entry === true || isPastShift || !commonStore?.config?.client?.data?.loginRules?.allowUserToEnableDisableAutoTimesheet || shift.allow_user_for_auto_timesheet === true),
        allow_shift_for_auto_timesheet: shift.allow_shift_for_auto_timesheet !== false,
        allow_user_for_auto_timesheet: shift.allow_user_for_auto_timesheet,
        isFutureScheduleEntry: shift.is_future_schedule_entry === true,
        createdFrom: shift.created_from || null,
        // recurring shifts can't be moved or copied, so no drag & drop
        movable: shift.created_from !== 'recurring'
    };
    if (shift.extraPayments) {
        event.extraPayments = shift.extraPayments;
    }
    return event;
};

export const transformToSchedulerEventList = (input, dateTimeRules, isModalView = false) => {
    if (isModalView) return { list: input?.shifts?.length ? input.shifts.map((r) => transformToSchedulerEvent(r, dateTimeRules)) : [] };
    return {
        list: input.combined.sort((a, b) => a.start_time - b.start_time).map((r) => transformToSchedulerEvent(r, dateTimeRules)),
    };
};
export const transformToSchedulerAvailability = (availabilityData, dateTimeRules) => {
    const availability = {
        id: availabilityData.id,
        availability_type: availabilityData.availability_type,
        from: availabilityData.from >= 0 ? timeFormat(availabilityData.from, dateTimeRules) : '',
        to: availabilityData.to >= 0 ? timeFormat(availabilityData.to, dateTimeRules) : '',
        resourceId: availabilityData.user_id || 0,
        title: `${timeFormat(availabilityData.from, dateTimeRules)} - ${timeFormat(availabilityData.to, dateTimeRules)}`,
        bgColor: '#000',
        showPopover: true,
        for_date: availabilityData.for_date,
        resourceName: `${availabilityData.first_name} - ${availabilityData.last_name}`,
        source: "availability"
    };
    return availability;
};

export const transformToSchedulerAvailabilityList = (input, dateTimeRules) => {
    return {
        list: input.availability.map((r) => transformToSchedulerAvailability(r, dateTimeRules)),
    };
};


export const getStatusText = (entity) => {
    let statusText = 'Waiting for invites';
    if (!entity.status) entity.status = scheduleStatuses.PENDING_DRAFT;
    switch (entity.status) {
        case scheduleStatuses.PENDING_INVITED:
            statusText = 'Invitations sent';
            break;
        case scheduleStatuses.PENDING_ACCEPTED:
            statusText = 'Invitation accepted';
            break;
        case scheduleStatuses.PENDING_USER_REJECTED:
            statusText = 'Rejected by the user';
            break;
        case scheduleStatuses.PENDING_USER_INVITED:
            statusText = 'Invitation sent by the user';
            break;
        case scheduleStatuses.PENDING_DRAFT:
            statusText = 'Pending';
            break;
        case scheduleStatuses.ASSIGNED:
            statusText = 'Assigned';
            break;
        default:
            statusText = 'Pending';
    }
    return statusText;
};

export const getShiftTotalHoursInMins = (from_time, to_time) => {
    const _start_time = parseInt(from_time);
    const _end_time = parseInt(to_time);
    if (_start_time > _end_time) return 1440 - _start_time + _end_time;
    else return _end_time - _start_time;
}

export const calculateResourceTotals = (resources, events) => {
    const totalsMap = new Map();
    const weeklyHoursMap = new Set();
    const dailyTracker = new Map();

    for (const event of events) {
        if (event.source !== 'shift') continue;
        const resourceId = event.resourceId;
        const forDate = event.forDate;
        if (!resourceId) continue;

        const duration = event.isMultiday ? (1440 - (parseInt(event.startTime) || 0)) + (parseInt(event.endTime) || 0) : (parseInt(event.endTime) || 0) - (parseInt(event.startTime) || 0);
        const validDuration = duration > 0 ? duration : 0;

        const currentTotal = totalsMap.get(resourceId) || 0;
        totalsMap.set(resourceId, currentTotal + validDuration);

        const dayKey = `${resourceId}-${forDate}`;
        const dayTotal = (dailyTracker.get(dayKey) || 0) + validDuration;
        dailyTracker.set(dayKey, dayTotal);

        if (dayTotal > 540) {
            weeklyHoursMap.add(resourceId);
            event.exceedsDailyLimit = true;
        }
    }

    return resources.map(resource => {
        const totalMinutes = totalsMap.get(resource.id) || 0;

        return {
            ...resource,
            total_time: totalMinutes,
            is_shift_available: totalMinutes > 0,
            has_daily_limit_exceeded: weeklyHoursMap.has(resource.id),
            is_weekly_overtime: totalMinutes > 2400
        };
    });
};